import { useCallback, useState } from 'react';
import { bomClient } from '../client/bomClient';

/**
 * "Run the analysis again" after a failed run: asks the server to start a
 * fresh one (POST /bom/:bomId/analysis-runs), then hands back to the caller
 * to resume polling. A run already going (200) or a last run that did not
 * fail (409) both mean there is something to poll, so they resume too.
 */
const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } })?.response?.status;

const messageOf = (err: unknown): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
  "I couldn't restart the analysis. Try again in a moment.";

export const useAnalysisRetry = (bomId: string | null, onRestarted: () => void) => {
  const [retryError, setRetryError] = useState<string | null>(null);

  const retry = useCallback(async () => {
    if (!bomId) return;
    setRetryError(null);
    try {
      await bomClient.retryAnalysis(bomId);
      onRestarted();
    } catch (err) {
      if (statusOf(err) === 409) {
        onRestarted();
        return;
      }
      setRetryError(messageOf(err));
    }
  }, [bomId, onRestarted]);

  return { retry, retryError };
};

export default useAnalysisRetry;
